import {
  Users, UserPlus, TrendingUp, Activity, Calendar,
  BarChart3, Repeat, MessageSquare, Heart, AlertTriangle,
} from 'lucide-react'
import { StatsCard } from '@/components/dashboard/StatsCard'
import { DashboardSectionHeader } from '@/components/dashboard/DashboardSectionHeader'
import { MetricChart } from '@/components/dashboard/MetricChart'
import { useUserStats } from '@/hooks/useDashboardData'
import { useUserTimeSeries } from '@/hooks/useUserTimeSeries'
import { CHART_BLUE, CHART_PURPLE } from '@/lib/chartColors'
import type { TimePeriod } from '@/types/dashboard'
import { formatPeriodLabel } from '@/types/dashboard'

interface UsersCommunityProps {
  period: TimePeriod
}

export function UsersCommunitySection({ period }: UsersCommunityProps) {
  const { data, isLoading } = useUserStats(period)
  const { data: userTimeSeries, isLoading: timeSeriesLoading } = useUserTimeSeries(period)

  const newUsersGrowth = period !== 'all' && data && data.previousPeriodNewUsers !== null && data.previousPeriodNewUsers > 0
    ? ((data.periodNewUsers - data.previousPeriodNewUsers) / data.previousPeriodNewUsers) * 100
    : period !== 'all' && data && data.periodNewUsers > 0 ? 100 : 0

  return (
    <div>
      <DashboardSectionHeader
        icon={Users}
        title="Users & Community"
      />
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-3">
        <StatsCard
          title="Total Users"
          value={data?.totalUniqueUsers.toLocaleString() || '0'}
          icon={Users}
          loading={isLoading}
        />
        <StatsCard
          title={period === 'all' ? 'New Users (all time)' : `New Users (${period})`}
          value={data?.periodNewUsers.toLocaleString() || '0'}
          change={period !== 'all' && newUsersGrowth !== 0 ? {
            value: Number(newUsersGrowth.toFixed(1)),
            trend: newUsersGrowth >= 0 ? 'up' : 'down',
            suffix: '%',
            label: formatPeriodLabel(period),
          } : undefined}
          icon={UserPlus}
          loading={isLoading}
        />
        {period !== 'all' && (
          <StatsCard
            title="User Growth Rate"
            value={`${newUsersGrowth.toFixed(1)}%`}
            change={newUsersGrowth !== 0 ? {
              value: Number(newUsersGrowth.toFixed(1)),
              trend: newUsersGrowth >= 0 ? 'up' : 'down',
              suffix: '%',
              label: `vs previous ${period}`,
            } : undefined}
            icon={TrendingUp}
            loading={isLoading}
          />
        )}
        <StatsCard
          title="DAU"
          value={data?.activeUsers24h.toLocaleString() || '0'}
          icon={Activity}
          loading={isLoading}
        />
        <StatsCard
          title="WAU"
          value={data?.activeUsers7d.toLocaleString() || '0'}
          icon={Activity}
          loading={isLoading}
        />
        <StatsCard
          title="MAU"
          value={data?.activeUsers30d.toLocaleString() || '0'}
          icon={Calendar}
          loading={isLoading}
        />
        <StatsCard
          title="DAU/MAU Ratio"
          value={data ? `${data.dauMauRatio.toFixed(1)}%` : '0.0%'}
          icon={BarChart3}
          loading={isLoading}
        />
        <StatsCard
          title="Creator Retention"
          value={data ? `${data.creatorRetentionRate.toFixed(1)}%` : '0.0%'}
          subtitle="Creators with 2+ tokens"
          icon={Repeat}
          loading={isLoading}
        />
        <StatsCard
          title="Total Comments"
          value={data?.totalComments.toLocaleString() || '0'}
          icon={MessageSquare}
          loading={isLoading}
        />
        <StatsCard
          title="Total Likes"
          value={data?.totalLikes.toLocaleString() || '0'}
          icon={Heart}
          loading={isLoading}
        />
        <StatsCard
          title="Reported Comments"
          value={data?.reportedCommentsCount.toLocaleString() || '0'}
          icon={AlertTriangle}
          warning={data && data.reportedCommentsCount > 0 ? `${data.reportedCommentsCount} reports to review` : undefined}
          loading={isLoading}
        />
      </div>
      <div className="mt-4 flex flex-col lg:flex-row gap-3 sm:gap-4">
        <div className="flex-1">
          <MetricChart
            type="line"
            title="User Growth"
            data={userTimeSeries?.userGrowth || []}
            color={CHART_BLUE}
            loading={timeSeriesLoading}
          />
        </div>
        <div className="flex-1">
          <MetricChart
            type="bar"
            title={period === 'all' ? 'Engagement (all time)' : `Engagement (${period})`}
            data={userTimeSeries?.dailyEngagement || []}
            color={CHART_PURPLE}
            loading={timeSeriesLoading}
          />
        </div>
      </div>
    </div>
  )
}
